import { DateFormats } from 'infrastructure/enums';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { usePatientCheckAvailability } from 'infrastructure/hooks/patient/use-patient-check-availability';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface IProps {
  patientGuid: string;
  withAlert?: boolean;
}

export const useAvailabilityAlert = ({ patientGuid, withAlert }: IProps) => {
  const { t } = useTranslation();
  const { mutateAsync, isLoading } = usePatientCheckAvailability();

  const { Alert, showAlert } = useAlert();

  const checkAvailability = useCallback(
    async (patientPhone: string) => {
      const data = await mutateAsync({ patientGuid, patientPhone });

      if (data) {
        const {
          isAvailable,
          isInWorkHours,
          contactAvailabilities,
          nextAvailableTime,
          patientTimezone,
          todaysHoliday,
        } = data.availability;

        let sendNow = false;
        let sendLater = false;
        const parsedTime = nextAvailableTime
          ? DateFormatter({
              date: convertDateToTimeZone(nextAvailableTime, patientTimezone),
              format: DateFormats['MM-dd-yyyy HH:mm:ss'],
            })
          : '';

        if (isAvailable && isInWorkHours) {
          return {
            isAvailable,
            isInWorkHours,
            sendNow: true,
            sendLater: false,
            contactAvailabilities,
            nextAvailableTime,
            patientTimezone,
            todaysHoliday,
          };
        }

        if (!isAvailable && isInWorkHours && withAlert) {
          await showAlert({
            title: t('smsDashboard.schedule_message'),
            messageTitle: t(
              'smsDashboard.contact_availability_not_available_message',
            ),
            message: (
              <Trans
                i18nKey="smsDashboard.send_now_or_at_next_available_time"
                values={{
                  time: parsedTime,
                }}
              />
            ),
            rejectBtnLabel: t('controls.sendNow'),
            successBtnLabel: t('controls.sendLater'),
            handleOk: () => {
              sendNow = false;
              sendLater = true;
            },
            handleCancel: (res: boolean | undefined) => {
              if (res !== undefined) {
                sendNow = true;
                sendLater = false;
              }
            },
          });
        }

        if (!isInWorkHours && withAlert) {
          await showAlert({
            title: t('smsDashboard.schedule_message'),
            messageTitle: t(
              'smsDashboard.contact_availability_not_business_hours_message',
            ),
            message: (
              <Trans
                i18nKey="smsDashboard.send_at_next_available_time"
                values={{
                  time: parsedTime,
                }}
              />
            ),
            successBtnLabel: t('controls.sendLater'),
            showReject: false,
            handleOk: () => {
              sendNow = false;
              sendLater = true;
            },
            handleCancel: (res: boolean | undefined) => {
              if (res !== undefined) {
                sendNow = true;
                sendLater = false;
              }
            },
          });
        }

        return {
          isAvailable,
          isInWorkHours,
          sendNow,
          sendLater,
          contactAvailabilities,
          nextAvailableTime,
          patientTimezone,
          todaysHoliday,
        };
      }
      return {
        isAvailable: false,
        isInWorkHours: false,
        sendNow: false,
        sendLater: false,
        contactAvailabilities: [],
        nextAvailableTime: null,
        patientTimezone: null,
        todaysHoliday: null,
      };
    },
    [withAlert, patientGuid, mutateAsync, t],
  );

  return {
    isLoading,
    checkAvailability,
    Alert,
  };
};
